'use client';

import React from 'react';
import {
  ShieldCheck,
  Globe,
  Database,
  RefreshCw,
  Sliders,
  FileText,
  ExternalLink,
  Calendar,
  AlertCircle,
  Building2,
} from 'lucide-react';
import { ToolTrustEntry } from '@/lib/trust-seo/trust-registry';
import type { ToolTruthPresentation } from '@/lib/external-truth';

interface ToolTrustBlockProps {
  entry: ToolTrustEntry;
  presentation?: ToolTruthPresentation;
  className?: string;
}

export function ToolTrustBlock({ entry, presentation, className = '' }: ToolTrustBlockProps) {
  const { trustMode, trustStatement, disclaimer, sources, freshLayer, facts, effectiveFrom, lastReviewed } = entry;

  // Visual styling variants per trust mode
  const getModeBadge = () => {
    switch (trustMode) {
      case 'VN_OFFICIAL_REGULATORY':
        return {
          icon: <ShieldCheck className="w-4 h-4 text-emerald-600" />,
          title: 'Căn Cứ Pháp Lý Đang Áp Dụng',
          pill: 'Văn Bản Quy Phạm Pháp Luật',
          pillBg: 'bg-emerald-50 text-emerald-800 border-emerald-200',
          borderBg: 'border-emerald-200 bg-emerald-50/30',
        };
      case 'INTERNATIONAL_AUTHORITY':
        return {
          icon: <Globe className="w-4 h-4 text-blue-600" />,
          title: 'Chuẩn Mực Thẩm Quyền Quốc Tế',
          pill: 'Tiêu Chuẩn Quốc Tế',
          pillBg: 'bg-blue-50 text-blue-800 border-blue-200',
          borderBg: 'border-blue-200 bg-blue-50/30',
        };
      case 'OFFICIAL_DATASET':
        return {
          icon: <Database className="w-4 h-4 text-indigo-600" />,
          title: 'Bộ Dữ Liệu Công Bố Chính Thức',
          pill: 'Danh Mục Quốc Gia',
          pillBg: 'bg-indigo-50 text-indigo-800 border-indigo-200',
          borderBg: 'border-indigo-200 bg-indigo-50/30',
        };
      case 'INTERNET_FRESHLAYER':
        return {
          icon: <RefreshCw className="w-4 h-4 text-sky-600" />,
          title: 'Dữ Liệu Internet Đồng Bộ Định Kỳ',
          pill: 'FreshLayer Sync',
          pillBg: 'bg-sky-50 text-sky-800 border-sky-200',
          borderBg: 'border-sky-200 bg-sky-50/30',
        };
      case 'MARKET_OR_COMMERCIAL':
        return {
          icon: <Building2 className="w-4 h-4 text-amber-600" />,
          title: 'Tham Chiếu Thị Trường & Thương Mại',
          pill: 'Chính Sách Dịch Vụ',
          pillBg: 'bg-amber-50 text-amber-800 border-amber-200',
          borderBg: 'border-amber-200 bg-amber-50/30',
        };
      case 'USER_INPUT_BY_DESIGN':
      default:
        return {
          icon: <Sliders className="w-4 h-4 text-purple-600" />,
          title: 'Thông Số Do Người Dùng Thiết Lập',
          pill: 'User-Input By Design',
          pillBg: 'bg-purple-50 text-purple-800 border-purple-200',
          borderBg: 'border-purple-200 bg-purple-50/30',
        };
    }
  };

  const badge = getModeBadge();
  const limited = presentation?.restriction === 'reference_only' || presentation?.restriction === 'deferred';

  return (
    <section
      aria-label="Khung minh bạch dữ liệu và căn cứ tin cậy"
      className={`rounded-2xl border ${badge.borderBg} p-5 sm:p-6 space-y-5 text-slate-800 ${className}`}
    >
      {/* Header Info */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200/80 pb-3.5">
        <div className="flex items-center gap-2.5">
          <div className="p-1.5 rounded-lg bg-white shadow-2xs border border-slate-200/80">
            {badge.icon}
          </div>
          <div>
            <h3 className="text-sm sm:text-base font-bold text-slate-900 leading-none">
              {limited ? presentation!.heading : badge.title}
            </h3>
            <p className="text-[11px] text-slate-500 mt-1">
              {limited ? presentation!.notice : `Đối soát minh bạch • Snapshot 09/2026 • Đã xác minh: ${lastReviewed}`}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${badge.pillBg}`}>
            {limited ? presentation!.statusLabel : badge.pill}
          </span>
        </div>
      </div>

      {/* Trust Statement & Facts Used */}
      <div className="space-y-2.5 text-xs sm:text-sm">
        <p className="text-slate-700 leading-relaxed font-medium">
          {limited ? presentation!.notice : trustStatement}
        </p>

        {facts && facts !== 'N/A' && (
          <div className="bg-white/80 p-3 rounded-xl border border-slate-200/70 text-xs space-y-1">
            <span className="font-semibold text-slate-900 block">Dữ kiện thực tế công cụ sử dụng:</span>
            <span className="text-slate-600 leading-relaxed">{facts}</span>
          </div>
        )}
      </div>

      {/* FreshLayer Metadata (For INTERNET_FRESHLAYER only - never mentions pha.vn) */}
      {freshLayer && !limited && (
        <div className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-2xs space-y-2 text-xs">
          <div className="flex items-center justify-between font-semibold text-slate-900 border-b border-slate-100 pb-1.5">
            <span>Thông số cập nhật FreshLayer:</span>
            <span className="text-emerald-700 font-bold bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200 text-[11px]">
              Trạng thái: {freshLayer.freshnessStatus}
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-slate-600 text-[11px]">
            <div><strong>Nguồn dữ liệu:</strong> {freshLayer.sourceLabel}</div>
            <div><strong>Chính sách làm mới:</strong> {freshLayer.refreshPolicy}</div>
            <div><strong>Ngày dữ liệu:</strong> {freshLayer.dataDate}</div>
            <div><strong>Kiểm tra gần nhất:</strong> {freshLayer.lastCheckedAt.split('T')[0]}</div>
          </div>
        </div>
      )}

      {/* Official Sources Cards (When sources exist) */}
      {sources && sources.length > 0 && (
        <div className="space-y-2.5">
          <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
            <FileText className="w-3.5 h-3.5 text-slate-500" />
            Văn Bản / Nguồn Căn Cứ Đối Chiếu ({sources.length})
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
            {sources.map((src, i) => (
              <div
                key={i}
                className="bg-white p-3 rounded-xl border border-slate-200/90 shadow-2xs space-y-1.5 flex flex-col justify-between"
              >
                <div className="space-y-0.5">
                  <div className="text-xs font-bold text-slate-900 flex items-start justify-between gap-2">
                    <span className="line-clamp-2">{src.documentTitle || src.sourceId}</span>
                  </div>
                  <div className="text-[11px] text-slate-500 space-y-0.5">
                    {src.documentNumber && <div>Số hiệu: <span className="font-medium text-slate-700">{src.documentNumber}</span></div>}
                    <div>Cơ quan ban hành: <span className="font-medium text-slate-700">{src.authority}</span></div>
                    {src.effectiveFrom && (
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3 text-slate-400" />
                        Có hiệu lực từ: <span className="font-medium text-slate-700">{src.effectiveFrom}</span>
                      </div>
                    )}
                  </div>
                </div>

                {src.officialUrl && (
                  <a
                    href={src.officialUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-600 hover:text-blue-800 hover:underline pt-1 self-start"
                  >
                    <span>Xem văn bản chính thức</span>
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Contextual Disclaimer */}
      <div className="bg-slate-100/70 p-3.5 rounded-xl border border-slate-200/60 text-slate-600 text-xs leading-relaxed flex items-start gap-2.5">
        <AlertCircle className="w-4 h-4 text-slate-500 shrink-0 mt-0.5" />
        <div>
          <span className="font-semibold text-slate-800">Lưu ý &amp; Miễn trừ trách nhiệm: </span>
          <span>{disclaimer}</span>
        </div>
      </div>
    </section>
  );
}
